import { AbstractControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import type { FormlyHookFn } from './formly-hook-fn.type';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const numericValue = 0;
const textValue = '';

const getCurrentIndex = (key: unknown): number => {
  const keyString = key?.toString() ?? '';
  const match = keyString.match(/_(\d+)$/);

  return match ? Number(match[1]) : NaN;
};

const getResetValue = (
  fieldConfig: FormlyFieldConfig | undefined,
  control: AbstractControl | null
) => {
  if (fieldConfig?.defaultValue !== undefined) {
    return fieldConfig.defaultValue;
  }

  const type = fieldConfig?.props?.type;
  if (
    type === 'number' ||
    type === 'currency' ||
    typeof control?.value === 'number'
  ) {
    return numericValue;
  }

  return textValue;
};

const findFieldConfig = (
  fields: FormlyFieldConfig[] | undefined,
  path: string[]
): FormlyFieldConfig | undefined => {
  if (!fields?.length || !path.length) {
    return undefined;
  }

  for (const field of fields) {
    if (field.key?.toString() === path[0]) {
      return path.length === 1
        ? field
        : findFieldConfig(field.fieldGroup, path.slice(1));
    }

    const child = findFieldConfig(field.fieldGroup, path);
    if (child) {
      return child;
    }
  }

  return undefined;
};

const clearSectionValues = (
  field: FormlyFieldConfig,
  sectionPrefix: string,
  endIndex: number,
  fieldsToClear: string[],
  dependentTogglePrefix: string | undefined,
  startIndex: number
) => {
  for (let index = startIndex; index <= endIndex; index++) {
    fieldsToClear.forEach((fieldKey) => {
      const controlPath = `${sectionPrefix}_${index}.${fieldKey}`;
      const control = field.form?.get(controlPath);
      const fieldConfig = findFieldConfig(
        field.parent?.fieldGroup,
        controlPath.split('.')
      );
      const resetValue = getResetValue(fieldConfig, control);

      control?.setValue(resetValue);

      if (fieldConfig?.model && fieldConfig.key) {
        fieldConfig.model[fieldConfig.key as string] = resetValue;
      }
    });

    const dependentToggle = field.form?.get(
      `${dependentTogglePrefix}_${index + 1}`
    );
    if (dependentToggle?.value !== false) {
      dependentToggle?.setValue(false);
    }
  }
};

export const clearHiddenSectionValuesHook = (
  splitFunctionName: string[],
  destroyed$: Observable<unknown>
): FormlyHookFn => {
  return (field: FormlyFieldConfig) => {
    const args = splitFunctionName[1]
      ?.split(',')
      .map((arg) => arg.trim())
      .filter(Boolean);

    const sectionPrefix = args?.[0];
    const endIndex = Number(args?.[1]);
    const fieldsToClear = args?.slice(2) ?? [];

    if (!sectionPrefix || Number.isNaN(endIndex) || !fieldsToClear.length) {
      return;
    }

    const startIndex = getCurrentIndex(field.key);
    if (Number.isNaN(startIndex)) {
      return;
    }

    const dependentTogglePrefix = field.key
      ?.toString()
      .replace(/_\d+$/, '');

    field.formControl?.valueChanges
      .pipe(takeUntil(destroyed$))
      .subscribe((value) => {
        if (value !== false) {
          return;
        }

        Promise.resolve().then(() =>
          clearSectionValues(
            field,
            sectionPrefix,
            endIndex,
            fieldsToClear,
            dependentTogglePrefix,
            startIndex
          )
        );
      });
  };
};
